import React, { useEffect, useState } from "react";
import { Drawer } from "antd";
import "./RenderDevEditForm.less";
import IntlMessages from "../../util/IntlMessages";
import { useIntl } from "react-intl";
import { Button, Input } from "../BP2Components";
import SearchMethod from "./components/SearchMethod/SearchMethod";
import ListSettings from "./components/ListSettings/ListSettings";
import CheckboxSection from "./components/CheckboxSection/CheckboxSection";
import PositionSection from "./components/PositionSection/PositionSection";
import { getDeep, READ_MAP } from "../../util/fieldMap";
import { useDevFormContext } from "../FormsV2/FormTab/DevFormContext";

export type tDevForm = {
   state: Partial<tState>;
   setField?: <K extends keyof tState>(name: K, val: tState[K]) => void;
   element?: any;
};

const NOT_LIST_SETTINGS_FIELD = [
   "saveas",
   "form",
   "button",
   "table",
   "listTable",
   "textarea",
   "podpis",
   "file",
   "chartSQL",
   "text",
];

const SETTINGS_SELECTED_FIELD = [
   "select",
   "multi_select",
   "radio",
   "checkbox",
   "sql",
   "client",
   "defineField",
   "text",
   "textarea",
];

export type tState = {
   lable: string; // element.object.UIP_OPIS

   // Search options
   seekingOut: "1" | "2" | "3" | "4"; // element.FormLoContent.seeking_out_type
   seekingOutWithSearch: boolean; // element.FormLoContent.seeking_out_with_search
   advansedSearch: boolean | string; // element.FormLoContent.advansed_search_list_id
   dedicatedSearchId: string;
   advansedSearchId: string;

   // List settings
   dropdownIsEmptyOption: boolean | number; // element.object.UIP_NUM4
   dropdownEmptyOptionLabel: string; // element.object.UIP_TEKST3
   dropdownEmptyOptionValue: string; // element.object.UIP_TEKST4

   // Checkboxes
   Subfolder: boolean; // element.object.UIP_TEKST1
   Required: boolean; // element.object.UIP_OBOW == 1
   Hidden: boolean; // element.hidden
   hiddenOnPrint: boolean; // element.object.hidden_on_print
   Transparency: boolean; // element.object.UIP_TRANSPARENCY == 1
   toggleTag: boolean; // element.toggleTag == 1
   Editable: boolean; // element.objecy.UIP_EDYT
   useAsTag: boolean; // UTB_TOGGLE_TAG // toggleTag

   barcode_support: boolean; // element.object.UIP_BARCODE_SUPPORT // barcode_support

   // Position
   position: "left" | "right" | "page_width"; // element.FormLoContent.page_position

   // Precision
   precision: string; // element.precision
};

export const RenderDevEditForm = ({ element }) => {
   const intl = useIntl();
   const [editIsOpen, setEditIsOpen] = useState<boolean>(false);
   const [data, setData] = useState<Partial<tState>>({});
   const patchDevElement = useDevFormContext();
   const precisionOptions = React.useMemo(
      () => [
         { value: "", label: intl.formatMessage({ id: "global.choose" }) },
         ...Array.from({ length: 11 }, (_, i) => ({ value: String(i), label: String(i) })),
      ],
      [intl],
   );

   useEffect(() => {
      if (!editIsOpen || !element?.id) return;
      const fields: Partial<tState> = {};
      (Object.keys(READ_MAP) as (keyof tState)[]).forEach((key) => {
         const cfg = READ_MAP[key];
         if (!cfg.path) return;
         const raw = getDeep(element, cfg.path);
         const val = cfg.parse ? cfg.parse(raw) : raw;
         fields[key] = val as any;
      });
      setData(fields);
   }, [editIsOpen, element?.id]);

   const setField = <K extends keyof tState>(name: K, value: tState[K]) => {
      setData((prev) => ({ ...prev, [name]: value }));
   };

   const onSave = () => {
      const patches = (Object.keys(data) as (keyof tState)[]).flatMap((key) => {
         const path = READ_MAP[key]?.path;
         return path ? [{ path, value: (data as any)[key] }] : [];
      });
      if (!element?.tech_name || patches.length === 0) return;
      patchDevElement(element.tech_name, patches);
      setEditIsOpen(false);
   };

   return (
      <div>
         <button className="dev-info" onClick={() => setEditIsOpen(!editIsOpen)}>
            <IntlMessages id="devForm.openEdit" />
         </button>

         <Drawer
            visible={editIsOpen}
            onClose={() => setEditIsOpen(false)}
            title={`${intl.formatMessage({ id: "devForm.editField" })} - ${element?.label} (${element?.tech_name ?? ""})`}
            placement="right"
            destroyOnClose
            className="dev-edit-drawer"
         >
            <div className="dev-field-name">
               <label>
                  <IntlMessages id="devForm.name" />:
               </label>
               <Input
                  type="text"
                  value={data.lable}
                  onChange={(e) => setField?.("lable", e.target.value)}
                  placeholder=""
               />
            </div>
            {(element.type === "sql" || element.type === "client" || element.type === "select") && (
               <SearchMethod state={data} setField={setField} element={element} />
            )}
            {!NOT_LIST_SETTINGS_FIELD.includes(element.type) && (
               <ListSettings state={data} setField={setField} element={element} />
            )}
            {SETTINGS_SELECTED_FIELD.includes(element.type) && (
               <CheckboxSection state={data} setField={setField} element={element} />
            )}
            <PositionSection state={data} element={element} setField={setField} />
            <div className="dev-decimal-precision">
               <label className="precision-label">
                  <IntlMessages id="devForm.decimalPrecision" />:
               </label>
               <select
                  id={`decimal-precision-${element?.tech_name || "field"}`}
                  value={data.precision}
                  onChange={(e) => setField("precision", e.target.value)}
               >
                  {precisionOptions.map((opt) => (
                     <option key={opt.value || "none"} value={opt.value}>
                        {opt.label}
                     </option>
                  ))}
               </select>
            </div>
            <div className="save-button">
               <Button buttonType={"primary"} onClick={onSave}>
                  <IntlMessages id="global.save" />
               </Button>
            </div>
         </Drawer>
      </div>
   );
};

export default RenderDevEditForm;
